import React from 'react'
import {
  Button,
  LinkButton,
  Skeleton,
  useForTablet,
  useModalContext,
} from '@sketch/components'
import { routes } from '@sketch/modules-common'

import { HiddenProfileBanner } from '../HiddenProfileBanner'
import { EditingProfileNavBar } from '../EditingProfileNavBar'
import {
  UpdateProfileModal,
  PublishProfileModal,
  UnpublishProfileModal,
} from '../../modals'
import {
  Wrapper,
  Actions,
  ProfileStatus,
  StyledText,
  TopBannersSkeleton,
  Divider,
} from './ProfileTopBanners.styles'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'
import { EyeIcon } from '../HiddenProfileBanner/HiddenProfileBanner.styles'
import { GlobeIcon } from '../EditingProfileNavBar/EditingProfileNavBar.styles'

interface ProfileTopBannersProps {
  workspace: WorkspaceMinimalFragment
  profileData: {
    identifier: string
    shortUrlName: string
  }
  isPublic: boolean
  isLoading: boolean
}

export const ProfileTopBanners: React.FC<ProfileTopBannersProps> = ({
  profileData,
  isPublic,
  workspace,
  isLoading,
}) => {
  const { showModal } = useModalContext()
  const isSmallerThanTablet = !useForTablet()

  if (isLoading) {
    return (
      <TopBannersSkeleton>
        <Skeleton height="32px" width="100px" />
        <Skeleton height="32px" width="100px" />
      </TopBannersSkeleton>
    )
  }

  const handlePublishProfile = () => {
    showModal(PublishProfileModal, {
      identifier: profileData.identifier,
    })
  }

  const handleUnpublishProfile = () => {
    showModal(UnpublishProfileModal, {
      identifier: profileData.identifier,
    })
  }

  const handleEditProfile = () => {
    showModal(UpdateProfileModal, {
      workspace,
    })
  }

  const renderPrivateActions = () => (
    <>
      <Button
        variant="secondary-untinted"
        size="32"
        onClick={handleEditProfile}
      >
        Edit
      </Button>
      <Button variant="primary" size="32" onClick={handlePublishProfile}>
        Publish
      </Button>
    </>
  )

  const renderPublicActions = () => (
    <>
      <Button
        variant="secondary-untinted"
        size="32"
        onClick={handleUnpublishProfile}
      >
        Hide
      </Button>
      <Button
        variant="secondary-untinted"
        size="32"
        onClick={handleEditProfile}
      >
        Edit
      </Button>
      <Divider />
      <LinkButton
        size="32"
        variant="secondary-untinted"
        href={routes.WORKSPACE_PROFILE.create({
          shortUrlName: profileData.shortUrlName || '',
        })}
        external
      >
        View ↗
      </LinkButton>
    </>
  )

  const renderMobileBanners = () => (
    <ProfileStatus>
      {isPublic ? (
        <>
          <GlobeIcon /> <StyledText>Public</StyledText>
        </>
      ) : (
        <>
          <EyeIcon /> <StyledText>Hidden</StyledText>
        </>
      )}
    </ProfileStatus>
  )

  const renderActions = () => (
    <Actions>
      {isPublic ? renderPublicActions() : renderPrivateActions()}
    </Actions>
  )

  const renderBanner = () => {
    if (isSmallerThanTablet) {
      return renderMobileBanners()
    }

    return isPublic ? (
      <EditingProfileNavBar />
    ) : (
      <HiddenProfileBanner shortUrlName={profileData.shortUrlName} />
    )
  }

  return (
    <Wrapper>
      {renderBanner()}
      {renderActions()}
    </Wrapper>
  )
}
